<template>
  <div>
    <div class="flex space-x-2">
      <div
        v-for="(value, index) in values"
        :key="index"
        class="flex"
      >
        <label
          :for="'input-' + index"
          class="sr-only"
        >
          {{ $t('login.code') }} {{ index }}
        </label>
        <input
          :key="index"
          :ref="fieldsRef[index]"
          type="tel"
          :data-id="index"
          :value="value"
          :disabled="disabled"
          :maxlength="codeLength"
          autocomplete="one-time-code"
          class="font-semibold size-11 text-base leading-6 text-center placeholder:text-neutral-400 text-neutral-700 transition py-0.5 px-2 border rounded-lg focus:ring-neutral-500 focus:border-neutral-500"
          :class="getClass(value)"
          @input="onInput"
          @keydown="onKeyDown"
          @paste="onPaste"
        >
      </div>
    </div>
    <span
      v-if="error"
      class="text-red-700 text-sm mt-1 block"
    >
      {{ error }}
    </span>
  </div>
</template>

<script>
export default {
  name: 'VetsterVerificationCodeInput',

  props: {
    codeLength: {
      type: Number,
      required: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    error: {
      type: String,
      default: '',
    },
  },

  data() {
    return {
      values: [],
      fieldsRef: [],
      isPasting: false,
    };
  },

  created() {
    this.initializeFields();
  },

  methods: {
    autofill(code) {
      const chars = code.split('');

      const values = this.values.map((currentValue, i) => {
        const char = chars[i];

        return char !== undefined && char !== currentValue
          ? char
          : currentValue;
      });

      this.setValues(values);
    },

    getClass(value) {
      return {
        'text-red-700 border-red-700 ring-red-700': this.error?.length > 0,
        'ring-neutral-200': !this.error?.length > 0,
        'border-neutral-200': !this.disabled || value,
        'border-neutral-500': this.disabled || value !== '',
      };
    },

    initializeFields() {
      for (let i = 0; i < this.codeLength; i += 1) {
        this.values.push('');
        this.fieldsRef.push(`ref_${i}`);
      }
    },

    onInput(event) {
      if (this.isPasting) {
        this.isPasting = false;
      } else if (event.data?.length > 1) {
        // Suggestions from Google Chrome will send many characters, truncated at <input maxlength>.
        this.autofill(event.data);
      } else {
        const { value } = event.target;

        if (value.length > 0) {
          this.setValues(this.truncateInput(value, +event.target.dataset.id));
        }
      }
    },

    onKeyDown(event) {
      const currentIndex = +event.target.dataset.id;
      const prevIndex = currentIndex - 1;
      const nextIndex = currentIndex + 1;
      const prevField = this.fieldsRef[prevIndex];
      const nextField = this.fieldsRef[nextIndex];

      switch (event.key) {
        case 'ArrowLeft':
          this.handleFocus(event, prevField);
          break;
        case 'ArrowRight':
          this.handleFocus(event, nextField);
          break;
        case 'Backspace': {
          event.preventDefault();
          const modifiedValues = [...this.values];
          if (this.values[currentIndex]) {
            modifiedValues[currentIndex] = '';
            this.values = modifiedValues;
            this.emitCode();
          } else if (prevField) {
            modifiedValues[prevIndex] = '';
            this.$refs[prevField][0].focus();
            this.values = modifiedValues;
            this.emitCode();
          }
          break;
        }
        default:
          break;
      }
    },

    onPaste(event) {
      this.isPasting = true;

      const pastedCode = event.clipboardData.getData('text');

      if (pastedCode) {
        this.autofill(pastedCode);
      }

      this.$nextTick(() => {
        this.isPasting = false;
      });
    },

    focusNextEmptyField() {
      const newFocusedFieldIndex = this.values.findIndex((element) => element === '');
      if (newFocusedFieldIndex > -1) {
        const field = this.fieldsRef[newFocusedFieldIndex];
        this.$refs[field][0].focus();
      }
    },

    handleFocus(event, field) {
      event.preventDefault();
      if (field) {
        this.$refs[field][0].focus();
      }
    },

    emitCode() {
      const code = this.values.join('');
      this.$emit('updated', code);
      if (code.length >= this.codeLength) {
        this.$emit('complete', code);
      }
    },

    setValues(values) {
      this.values = values;

      this.$nextTick(() => {
        this.focusNextEmptyField();
        this.emitCode();
      });
    },

    truncateInput(value, i) {
      const values = [...this.values];

      values[i] = value.length > 1
        ? value.replace(values[i], '')
        : value;

      return values;
    },
  },
};
</script>
